import React, { useEffect, useState } from "react";
import { useOrderContext } from "../../hooks/useGenerateOrder";
import AddAddress from "../AddAddressForm/address";
import { useDispatch } from "react-redux";
import { addAddress } from "slices/thunk";
import Tab from "Common/Components/Tab/Tab";
import { Nav } from "Common/Components/Tab/Nav";
import Alert from "Common/Components/Alert";
import { Loader2 } from "lucide-react";

const UserDetail = () => {
  const {
    handleGetUser,
    userEmail,
    setUserEmail,
    user,
    billingAddresses,
    shippingAddresses,
    selectedAddress,
    setSelectedAddress,
    userAddresses,
    userExists,
    createUserValue,
    handleCreateUserSubmit,
    setUserAddresses,
    addressOptions,
    setAddressOptions,
    toggleForm,
    handleAddressChange,
    generateOrderValue,
    isLoadingUser,
  } = useOrderContext();

  const handleTypeChange = (type: "billing" | "shipping", value: string) => {
    setAddressOptions((prevState) => ({
      ...prevState,
      [type]: value, // Update only the selected type (billing/shipping)
    }));

    if (value === "addBillingAddress") {
      // Clear the selected billing address when switching to "Add New Address"
      setSelectedAddress((prev) => ({ ...prev, billing: "", shipping: "" }));
    }
    if (type === "shipping" && value === "addShippingAddress") {
      // Reset shipping address when adding a new shipping address
      setUserAddresses((prev: any) => ({
        ...prev,
        shipping: {}, // Reset shipping address to empty
      }));
    }
  };
  useEffect(() => {
    if (addressOptions.shipping === "sameAsBillingAddress") {
      setUserAddresses((prev) => ({
        ...prev,
        shipping: prev.billing, // Set shipping to match billing
      }));
    }
  }, [userAddresses.billing, addressOptions.shipping]);

  const handleSelectedAddress = (type: "billing" | "shipping", id: string) => {
    setSelectedAddress((prev) => {
      let updatedState = { ...prev, [type]: id };

      if (type === "billing") {
        const selectedBilling = billingAddresses.find(
          (addr: any) => addr.id === id
        );

        if (selectedBilling) {
          generateOrderValue.setFieldValue("billingAddress", selectedBilling);

          const matchShippingAddress = shippingAddresses.find(
            (addr: any) =>
              addr.email === selectedBilling.email &&
              addr.zipCode === selectedBilling.zipCode &&
              addr.state === selectedBilling.state &&
              addr.city === selectedBilling.city &&
              addr.country === selectedBilling.country &&
              addr.streetAddress === selectedBilling.streetAddress
          );

          if (matchShippingAddress) {
            updatedState.shipping = matchShippingAddress.value;
            generateOrderValue.setFieldValue(
              "shippingAddress",
              matchShippingAddress
            );
          } else {
            updatedState.shipping = "";
            generateOrderValue.setFieldValue("shippingAddress", "");
          }
        }
      } else if (type === "shipping") {
        const selectedShipping = shippingAddresses.find(
          (addr: any) => addr.id === id
        );
        if (selectedShipping) {
          generateOrderValue.setFieldValue("shippingAddress", selectedShipping);
        }
      }

      return updatedState;
    });
  };

  const handleAddAddress = () => {
    // Check if the user is adding a new billing address
    if (addressOptions.billing === "addBillingAddress") {
      const billingAddress = userAddresses.billing; // Get the billing address from state
      generateOrderValue.setFieldValue("billingAddress", billingAddress); // Set billing address in generateOrderValue
    }

    // Check if the user is adding a new shipping address
    if (addressOptions.shipping === "addShippingAddress") {
      const shippingAddress = userAddresses.shipping; // Get the shipping address from state
      generateOrderValue.setFieldValue("shippingAddress", shippingAddress); // Set shipping address in generateOrderValue
    }
    // If the shipping address is the same as the billing address, set it accordingly
    if (addressOptions.shipping === "sameAsBillingAddress") {
      const billingAddress = userAddresses.billing; // Get the billing address from state
      generateOrderValue.setFieldValue("shippingAddress", billingAddress); // Set shipping address to be the same as billing
    }
  };
  // Effect to update shipping address when billing address changes
  useEffect(() => {
    if (
      addressOptions.shipping === "sameAsBillingAddress" &&
      userAddresses.billing
    ) {
      setUserAddresses((prev) => ({
        ...prev,
        shipping: {
          // ...prev.shipping,
          streetAddress: userAddresses.billing.streetAddress,
          city: userAddresses.billing.city,
          state: userAddresses.billing.state,
          zipCode: userAddresses.billing.zipCode,
          country: userAddresses.billing.country,
          firstName: userAddresses.billing.firstName,
          lastName: userAddresses.billing.lastName,
          companyName: userAddresses.billing.companyName,
          phone: userAddresses.billing.phone,
          email: userAddresses.billing.email,
        },
      }));
    }
  }, [userAddresses.billing, addressOptions.shipping]);

  // Check if all fields are filled for both addresses
  const isButtonDisabled =
    addressOptions.billing === "chooseBillingAddress" &&
    addressOptions.shipping === "chooseShippingAddress";

  return (
    <div>
      <div className="card p-5">
        <div className="pb-4 pb-2 text-[16px] font-semibold">User Details</div>
        <hr />
        {!user && (
          <div className="pt-5">
            <div>
              <label
                htmlFor="userEmailId"
                className="inline-block mb-2 text-base font-medium"
              >
                Email Id
              </label>
              <div className="flex gap-x-3 items-center">
                <input
                  type="text"
                  id="userEmailId"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Email id"
                  name="emailId"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
                <button
                  type="button"
                  onClick={handleGetUser}
                  className="h-[35px] px-4 whitespace-nowrap bg-custom-500 text-white rounded hover:bg-custom-600"
                >
                  Get User Info
                </button>
              </div>
              {generateOrderValue.errors.userId &&
                generateOrderValue.touched.userId && (
                  <div className="text-red-500 text-sm mt-1">
                    {generateOrderValue.errors.userId}
                  </div>
                )}
            </div>
          </div>
        )}
        {isLoadingUser ? (
          <div className="relative flex items-center justify-center min-h-full">
            <div className="flex justify-center fixed top-[50%]">
              <Loader2 className="size-[35px] ltr:mr-2 rtl:ml-2 animate-spin" />
            </div>
          </div>
        ) : (
          <>
            {userEmail && user && userExists && (
              <div className="mt-4 p-3 border">
                <p className="text-base flex gap-x-4 text-slate-800">
                  <img className="size-14 rounded shrink-0" src={user?.image} />
                  <div>
                    <p className="font-semibold"> {user?.name}</p>
                    <p> {user?.number}</p>
                    <p> {user?.email}</p>
                  </div>
                </p>
              </div>
            )}

            {userExists === false && (
              <div>
                {" "}
                <div className="pt-4">
                  <Alert className="flex gap-1 px-4 py-3 text-sm text-yellow-500 border border-yellow-200 rounded-md md:items-center bg-yellow-50 dark:bg-yellow-400/20 dark:border-yellow-500/50">
                    <Alert.Icon />
                    <Alert.Bold>User does not exist!</Alert.Bold> You can create
                    one by filling out the form below.
                  </Alert>
                </div>
                <form>
                  <div className="grid pt-5 grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
                    <div className="xl:col-span-6">
                      <label
                        htmlFor="name"
                        className="inline-block mb-2 text-base font-medium"
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        className="form-input"
                        placeholder="Name"
                        name="name"
                        onChange={createUserValue.handleChange}
                        value={createUserValue.values.name}
                      />
                      {createUserValue.errors.name &&
                        createUserValue.touched.name && (
                          <div className="text-red-500 text-sm mt-1">
                            {createUserValue.errors.name}
                          </div>
                        )}
                    </div>
                    <div className="xl:col-span-6">
                      <label
                        htmlFor="email"
                        className="inline-block mb-2 text-base font-medium"
                      >
                        Email
                      </label>
                      <input
                        type="text"
                        id="email"
                        className="form-input"
                        placeholder="email"
                        name="email"
                        onChange={createUserValue.handleChange}
                        value={createUserValue.values.email}
                      />
                      {createUserValue.errors.email &&
                        createUserValue.touched.email && (
                          <div className="text-red-500 text-sm mt-1">
                            {createUserValue.errors.email}
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="mt-5 px-5 py-2 bg-blue-500 text-white rounded-md"
                      onClick={(e) => handleCreateUserSubmit(e)}
                    >
                      Create User
                    </button>
                  </div>
                </form>
              </div>
            )}

            {userEmail && user && userExists && (
              <div className="pt-5">
                <p className="text-[16px] pb-5 font-semibold">Address</p>
                <div className="flex gap-x-2">
                  <div className="w-1/2">
                    <p className="mb-0">
                      {" "}
                      <div className="flex gap-2">
                        <div className="flex items-center gap-2">
                          <input
                            id="radioBordered1"
                            className="size-4 bg-white border rounded-full appearance-none cursor-pointer dark:bg-zink-700 border-slate-200 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-400 dark:checked:border-custom-400"
                            type="radio"
                            name="InlineRadio"
                            value="chooseBillingAddress"
                            // onChange={(e) => setAddressOptions(e.target.value)}
                            onChange={(e) =>
                              handleTypeChange("billing", e.target.value)
                            }
                          />
                          <label
                            htmlFor="radioBordered1"
                            className="align-middle"
                          >
                            Choose Address
                          </label>
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            id="radioBordered2"
                            className="size-4 bg-white border rounded-full appearance-none cursor-pointer dark:bg-zink-700 border-slate-200 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-400 dark:checked:border-custom-400"
                            type="radio"
                            name="InlineRadio"
                            value="addBillingAddress"
                            // onChange={(e) => setAddressOptions(e.target.value)}
                            onChange={(e) =>
                              handleTypeChange("billing", e.target.value)
                            }
                            onClick={() => toggleForm("billing")}
                            defaultChecked
                          />
                          <label
                            htmlFor="radioBordered2"
                            className="align-middle"
                          >
                            Add new address
                          </label>
                        </div>
                      </div>
                    </p>
                    {addressOptions.billing === "chooseBillingAddress" && (
                      <div className="pt-5">
                        {billingAddresses.map((address: any, index: number) => {
                          return (
                            <>
                              <div className="flex items-center mb-2 gap-2">
                                <input
                                  id={`radioBordered${address.id}`}
                                  className="size-3 bg-white shrink-0 border rounded-full appearance-none cursor-pointer dark:bg-zink-700 border-slate-200 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-400 dark:checked:border-custom-400"
                                  type="radio"
                                  name="billingAddresses"
                                  value={address.id}
                                  checked={
                                    addressOptions.billing ===
                                      "chooseBillingAddress" &&
                                    selectedAddress.billing === address.id
                                  }
                                  onChange={() =>
                                    handleSelectedAddress("billing", address.id)
                                  }
                                />
                                <label
                                  htmlFor={`radioBordered${address.id}`}
                                  className="flex gap-2 text-sm text-slate-500 dark:text-zink-200"
                                >
                                  {address.companyName}
                                  {address.companyName ? "-" : ""}
                                  {address.streetAddress} {address.city},
                                  {address.state}, {address.country} -
                                  {address.zipCode}
                                </label>
                              </div>
                            </>
                          );
                        })}
                      </div>
                    )}

                    {addressOptions.billing === "addBillingAddress" && (
                      <AddAddress
                        type="billing"
                        onAddressChange={(address) =>
                          handleAddressChange(address, "billing")
                        }
                      />
                    )}
                  </div>
                  <div className="w-1/2">
                    <div className="mb-0">
                      <div className="flex gap-2">
                        <div className="flex items-center gap-2">
                          <input
                            id="radioBordered3"
                            className="size-4 bg-white shrink-0 border rounded-full appearance-none cursor-pointer dark:bg-zink-700 border-slate-200 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-400 dark:checked:border-custom-400"
                            type="radio"
                            name="shippingOption"
                            value="sameAsBillingAddress"
                            onChange={(e) =>
                              handleTypeChange("shipping", e.target.value)
                            }
                          />
                          <label
                            htmlFor="radioBordered3"
                            className="align-middle"
                          >
                            Same as billing address
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            id="radioBordered4"
                            className="size-4 bg-white shrink-0 border rounded-full appearance-none cursor-pointer dark:bg-zink-700 border-slate-200 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-400 dark:checked:border-custom-400"
                            type="radio"
                            name="shippingOption"
                            value="chooseShippingAddress"
                            onChange={(e) =>
                              handleTypeChange("shipping", e.target.value)
                            }
                            // onChange={(e) => setAddressOptions(e.target.value)}
                          />
                          <label
                            htmlFor="radioBordered4"
                            className="align-middle"
                          >
                            Choose Address
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            id="radioBordered5"
                            className="size-4 bg-white shrink-0 border rounded-full appearance-none cursor-pointer dark:bg-zink-700 border-slate-200 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-400 dark:checked:border-custom-400"
                            type="radio"
                            name="shippingOption"
                            value="addShippingAddress"
                            onChange={(e) =>
                              handleTypeChange("shipping", e.target.value)
                            }
                            onClick={() => toggleForm("shipping")}
                            defaultChecked
                          />
                          <label
                            htmlFor="radioBordered5"
                            className="align-middle"
                          >
                            Add new address
                          </label>
                        </div>
                      </div>
                    </div>
                    {addressOptions.shipping === "sameAsBillingAddress" &&
                      addressOptions.billing === "addBillingAddress" && (
                        <AddAddress
                          type="shipping"
                          onAddressChange={(address) =>
                            handleAddressChange(address, "shipping")
                          }
                          initialValues={{
                            streetAddress:
                              userAddresses.billing?.streetAddress || "",
                            city: userAddresses.billing?.city || "",
                            state: userAddresses.billing?.state || "",
                            zipCode: userAddresses.billing?.zipCode || "",
                            country: userAddresses.billing?.country || "",
                            firstName: userAddresses.billing?.firstName || "",
                            lastName: userAddresses.billing?.lastName || "",
                            companyName:
                              userAddresses.billing?.companyName || "",
                            phone: userAddresses.billing?.phone || "",
                            email: userAddresses.billing?.email || "",
                          }}
                        />
                      )}
                    {addressOptions.shipping === "sameAsBillingAddress" &&
                    selectedAddress.billing
                      ? (() => {
                          // Get selected billing address dynamically
                          const selectedBilling = billingAddresses.find(
                            (addr: any) => addr.id === selectedAddress.billing
                          );

                          // Check if there's a matching shipping address dynamically
                          const hasMatchingShipping =
                            selectedBilling &&
                            shippingAddresses.some(
                              (addr: any) =>
                                addr.email === selectedBilling.email &&
                                addr.zipCode === selectedBilling.zipCode &&
                                addr.state === selectedBilling.state &&
                                addr.city === selectedBilling.city &&
                                addr.country === selectedBilling.country &&
                                addr.streetAddress ===
                                  selectedBilling.streetAddress
                            );

                          // If a matching shipping address is found, display it
                          if (hasMatchingShipping) {
                            const matchedShippingAddress =
                              shippingAddresses.find(
                                (addr: any) =>
                                  addr.email === selectedBilling.email &&
                                  addr.zipCode === selectedBilling.zipCode &&
                                  addr.state === selectedBilling.state &&
                                  addr.city === selectedBilling.city &&
                                  addr.country === selectedBilling.country &&
                                  addr.streetAddress ===
                                    selectedBilling.streetAddress
                              );

                            return (
                              <div className="flex items-center pt-5 gap-2">
                                <input
                                  id={`radioBordered${matchedShippingAddress.id}`}
                                  className="size-3 bg-white shrink-0 border rounded-full appearance-none cursor-pointer dark:bg-zink-700 border-slate-200 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-400 dark:checked:border-custom-400"
                                  type="radio"
                                  name="shippingAddresses"
                                  value={matchedShippingAddress.id}
                                  checked={true} // Auto-selected
                                  readOnly // Prevent manual selection
                                />
                                <label
                                  htmlFor={`radioBordered${matchedShippingAddress.id}`}
                                  className="flex gap-2 cursor-pointer text-sm text-slate-500 dark:text-zink-200"
                                >
                                  {matchedShippingAddress.companyName}
                                  {matchedShippingAddress.companyName
                                    ? "-"
                                    : ""}
                                  {matchedShippingAddress.streetAddress}{" "}
                                  {matchedShippingAddress.city},
                                  {matchedShippingAddress.state},{" "}
                                  {matchedShippingAddress.country} -
                                  {matchedShippingAddress?.zipCode}
                                </label>
                              </div>
                            );
                          } else {
                            // If no matching shipping address is found, show the shipping address form
                            return (
                              <AddAddress
                                type="shipping"
                                onAddressChange={(address) =>
                                  handleAddressChange(address, "shipping")
                                }
                                initialValues={selectedBilling} // Pass the selected billing address as initial values
                              />
                            );
                          }
                        })()
                      : null}
                    {addressOptions.shipping === "chooseShippingAddress" && (
                      <div className="pt-5">
                        {shippingAddresses.map(
                          (address: any, index: number) => {
                            return (
                              <>
                                <div className={`flex items-center mb-4 gap-2`}>
                                  <input
                                    id={`radioBordered${address.id}`}
                                    className="size-3 bg-white shrink-0 border rounded-full appearance-none cursor-pointer dark:bg-zink-700 border-slate-200 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-400 dark:checked:border-custom-400"
                                    type="radio"
                                    name="shippingAddresses"
                                    value={address.id}
                                    checked={
                                      addressOptions.shipping ===
                                        "chooseShippingAddress" &&
                                      selectedAddress.shipping === address.id
                                    }
                                    onChange={() =>
                                      handleSelectedAddress(
                                        "shipping",
                                        address.id
                                      )
                                    }
                                  />
                                  <label
                                    htmlFor={`radioBordered${address.id}`}
                                    className={`flex gap-2 cursor-pointer text-sm text-slate-500 dark:text-zink-200`}
                                  >
                                    {address.companyName}{" "}
                                    {address.companyName ? "-" : ""}
                                    {address.streetAddress} {address.city},{" "}
                                    {address.state}, {address.country} -{" "}
                                    {address?.zipCode}
                                    {/* {address.label} */}
                                  </label>
                                </div>
                              </>
                            );
                          }
                        )}
                      </div>
                    )}

                    {addressOptions.shipping === "addShippingAddress" && (
                      <AddAddress
                        type="shipping"
                        onAddressChange={(address) =>
                          handleAddressChange(address, "shipping")
                        }
                      />
                    )}
                  </div>
                </div>
                {(addressOptions.billing === "addBillingAddress" ||
                  addressOptions.shipping === "addShippingAddress" ||
                  (addressOptions.billing === "addBillingAddress" &&
                    addressOptions.shipping === "sameAsBillingAddress")) && (
                  <div className="pt-3 flex justify-end">
                    <button
                      type="button"
                      className={`h-[35px]
                     ${
                       isButtonDisabled
                         ? "opacity-50 cursor-not-allowed pointer-events-none"
                         : ""
                     }
                 px-4 whitespace-nowrap bg-custom-500 text-white rounded hover:bg-custom-600`}
                      onClick={handleAddAddress}
                      disabled={isButtonDisabled}
                    >
                      Add Address
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserDetail;
